import React, {
  useState,
  useMemo,
  useCallback,
  useTransition,
  useEffect,
  useRef,
} from "react";
import { Layout } from "antd";
import { Helmet } from "react-helmet";
import StreamingSidebar from "./StreamingSidebar/StreamingSidebar";
import VideoGrid from "./StreamingContent/VideoGrid";
import {
  useGetModDataQuery,
  useStartStreamMutation,
  useStopStreamMutation,
} from "../../Redux/service/mod-streaming/ModData";
import usePermission from "../../hooks/usePermission";
import AccessDenied from "../AccessDenied";
import "./ModStreaming.css";
import BreadCrumb from "../Header/SecondHeader";
import { toast } from "react-toastify";
import trans from "../../utils";

const { Content } = Layout;

const ModStreaming = () => {
  const { view } = usePermission("MODLIVESTREAM");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  // Prepare API query parameters - ensure serializable for RTK Query cache key
  const queryParams = useMemo(() => {
    const params = {};
    if (searchQuery && searchQuery.trim()) {
      params.search = searchQuery.trim();
    }
    if (selectedGroup && selectedGroup !== "all") {
      // Handle "ungrouped" option
      params.groupName =
        selectedGroup === "ungrouped" ? "ungrouped" : selectedGroup;
    }
    // Return empty object if no params to ensure consistent cache key
    return Object.keys(params).length > 0 ? params : {};
  }, [searchQuery, selectedGroup]);

  const {
    data: modData,
    isLoading: modDataLoading,
    isFetching: modDataFetching,
  } = useGetModDataQuery(queryParams, {
    // Refetch when parameters change
    refetchOnMountOrArgChange: true,
  });
  const [startStream] = useStartStreamMutation();
  const [stopStream] = useStopStreamMutation();

  // Extract groups and devices from the transformed data
  const groupsData = modData?.groups || [];
  const vehiclesData = modData?.devices || [];
  // Show loading during initial load or when fetching with new parameters
  const vehiclesLoading = modDataLoading || modDataFetching;
  const groupsLoading = modDataLoading || modDataFetching;
  const [activeStreams, setActiveStreams] = useState([]);
  const activeStreamsRef = useRef([]);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isPending, startTransition] = useTransition();

  // Keep ref in sync with state
  useEffect(() => {
    activeStreamsRef.current = activeStreams;
  }, [activeStreams]);

  // Process vehicles data
  const processedVehicles = useMemo(() => {
    if (!vehiclesData || vehiclesData.length === 0) return [];

    return vehiclesData.map((vehicle) => {
      // Use channels from mdvr if available, otherwise generate default channels
      const channels = [];
      const mdvr = vehicle.mdvr || {};
      const mdvrChannels = mdvr.channels || [];
      const channelCount = mdvr.channelCount || mdvrChannels.length || 4;

      if (mdvrChannels.length > 0) {
        // Use actual channels from mdvr
        mdvrChannels.forEach((channel) => {
          channels.push({
            number: channel.channelNumber,
            name: channel.name || `CH${channel.channelNumber}`,
            position: channel.position,
            isActive: channel.isActive,
            hlsUrl:
              vehicle.hlsUrl ||
              `blob:https://stream.softsprint.ae/c3c0960c-0a96-4325-a4dc-958083bcb6cf`,
          });
        });
      } else {
        // Generate default channels if none available
        for (let i = 1; i <= channelCount; i++) {
          channels.push({
            number: i,
            name: `CH${i}`,
            hlsUrl:
              vehicle.hlsUrl ||
              `blob:https://stream.softsprint.ae/c3c0960c-0a96-4325-a4dc-958083bcb6cf`,
          });
        }
      }

      const imei = vehicle.uniqueId || vehicle.imei || vehicle.id || "";

      return {
        id: vehicle.id,
        name: vehicle.name || imei,
        imei: imei,
        status: vehicle.status || "offline",
        channels,
        groupId: vehicle.groupId || null,
        mdvr: mdvr,
      };
    });
  }, [vehiclesData]);

  // Filter vehicles based on status (search and group are handled by API)
  const filteredVehicles = useMemo(() => {
    let filtered = processedVehicles;

    // Status filter (client-side since API doesn't handle it)
    if (selectedStatus !== "all") {
      filtered = filtered.filter(
        (vehicle) =>
          vehicle.status.toLowerCase() === selectedStatus.toLowerCase(),
      );
    }

    return filtered;
  }, [processedVehicles, selectedStatus]);

  // Group vehicles by group
  const groupedVehicles = useMemo(() => {
    const grouped = {};
    filteredVehicles.forEach((vehicle) => {
      const groupKey = vehicle.groupId ? String(vehicle.groupId) : "ungrouped";
      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(vehicle);
    });
    return grouped;
  }, [filteredVehicles]);

  // Create a Set of active stream IDs for O(1) lookup - memoized
  const activeStreamIds = useMemo(() => {
    return new Set(activeStreams.map((s) => s.id));
  }, [activeStreams]);

  // Optimized channel active check - O(1) lookup
  const isChannelActive = useCallback(
    (vehicleId, channelNumber) => {
      const streamId = `${vehicleId}-${channelNumber}`;
      return activeStreamIds.has(streamId);
    },
    [activeStreamIds],
  );

  // Handle channel selection - optimized with batch updates and transitions
  const handleChannelClick = useCallback(
    async (vehicle, channel) => {
      const streamId = `${vehicle.id}-${channel.number}`;

      startTransition(() => {
        setActiveStreams((prevStreams) => {
          // Toggle stream - if exists, remove it; if not, add it
          const existingIndex = prevStreams.findIndex((s) => s.id === streamId);

          if (existingIndex >= 0) {
            // Remove stream - stop it first
            const streamToRemove = prevStreams[existingIndex];
            if (
              streamToRemove &&
              streamToRemove.imei &&
              streamToRemove.channelNumber !== undefined
            ) {
              // Call stop API asynchronously (don't wait for it)
              stopStream({
                imei: streamToRemove.imei,
                channel: streamToRemove.channelNumber,
                dataType: streamToRemove.dataType ?? 0,
                streamType: streamToRemove.streamType ?? 0,
              }).catch((error) => {
                console.error("Failed to stop stream:", error);
              });
            }

            // Remove stream and reindex remaining streams
            const filtered = prevStreams.filter((s) => s.id !== streamId);
            return filtered.map((stream, index) => ({
              ...stream,
              gridIndex: index,
            }));
          } else {
            // Add stream (max 32 streams)
            if (prevStreams.length >= 32) {
              return prevStreams;
            }

            // Calculate position - index will be used for dynamic grid
            const index = prevStreams.length;

            // Initially add stream with placeholder URL, will update after API call
            const newStream = {
              id: streamId,
              vehicleName: vehicle.name,
              imei: vehicle.imei,
              channel: channel.name,
              channelNumber: channel.number,
              streamUrl: channel.hlsUrl, // Placeholder, will be updated
              gridIndex: index,
              isLoading: true,
              dataType: 0,
              streamType: 0,
            };

            // Start the stream via API
            startStream({
              imei: vehicle.imei,
              channel: channel.number,
              dataType: 0,
              streamType: 0,
            })
              .unwrap()
              .then((response) => {
                // Update stream with both webrtc and hls URLs from response
                if (
                  response.success &&
                  (response.urls?.webrtc || response.urls?.hls)
                ) {
                  setActiveStreams((prevStreams) => {
                    return prevStreams.map((stream) => {
                      if (stream.id === streamId) {
                        return {
                          ...stream,
                          streamUrl: response.urls.webrtc || response.urls.hls, // Try webrtc first, fallback to hls
                          webrtcUrl: response.urls.webrtc,
                          hlsUrl: response.urls.hls,
                          isLoading: false,
                          streamId: response.streamId,
                        };
                      }
                      return stream;
                    });
                  });
                } else {
                  // If API call fails, remove the stream
                  setActiveStreams((prevStreams) => {
                    const filtered = prevStreams.filter(
                      (s) => s.id !== streamId,
                    );
                    return filtered.map((stream, index) => ({
                      ...stream,
                      gridIndex: index,
                    }));
                  });
                  toast.error(
                    trans("Failed to start stream for ") +
                      vehicle.name +
                      " - " +
                      channel.name,
                  );
                }
              })
              .catch((error) => {
                toast.error(
                  trans("Failed to start stream for ") +
                    vehicle.name +
                    " - " +
                    channel.name,
                );
                console.error("Failed to start stream:", error);
                // Remove stream on error
                setActiveStreams((prevStreams) => {
                  const filtered = prevStreams.filter((s) => s.id !== streamId);
                  return filtered.map((stream, index) => ({
                    ...stream,
                    gridIndex: index,
                  }));
                });
              });

            return [...prevStreams, newStream];
          }
        });
      });
    },
    [startTransition, startStream, stopStream],
  );

  // Cleanup: Stop all streams when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      // Cleanup function runs on unmount
      // Use ref to get current streams since state might be stale
      const currentStreams = activeStreamsRef.current;
      currentStreams.forEach((stream) => {
        if (stream.imei && stream.channelNumber !== undefined) {
          stopStream({
            imei: stream.imei,
            channel: stream.channelNumber,
            dataType: stream.dataType ?? 0,
            streamType: stream.streamType ?? 0,
          }).catch((error) => {
            console.error("Failed to stop stream on cleanup:", error);
          });
        }
      });
    };
  }, [stopStream]);

  // Handle removing a stream
  const handleRemoveStream = useCallback(
    (streamId) => {
      setActiveStreams((prevStreams) => {
        const streamToRemove = prevStreams.find((s) => s.id === streamId);

        // Stop the stream via API
        if (
          streamToRemove &&
          streamToRemove.imei &&
          streamToRemove.channelNumber !== undefined
        ) {
          stopStream({
            imei: streamToRemove.imei,
            channel: streamToRemove.channelNumber,
            dataType: streamToRemove.dataType ?? 0,
            streamType: streamToRemove.streamType ?? 0,
          }).catch((error) => {
            console.error("Failed to stop stream:", error);
          });
        }

        const filtered = prevStreams.filter((s) => s.id !== streamId);
        // Reindex remaining streams
        return filtered.map((stream, index) => ({
          ...stream,
          gridIndex: index,
        }));
      });
    },
    [stopStream],
  );

  if (!view) {
    return <AccessDenied />;
  }

  return (
    <>
      <Helmet>
        <title>Mod Streaming - Fleet Command</title>
        <meta name="description" content="Live video streaming module" />
      </Helmet>
      <BreadCrumb />

      <div className="mod-streaming-wrapper">
        <Layout className="mod-streaming-container">
          <StreamingSidebar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            vehicles={filteredVehicles}
            groupedVehicles={groupedVehicles}
            groups={groupsData || []}
            onChannelClick={handleChannelClick}
            isChannelActive={isChannelActive}
            isLoading={vehiclesLoading || groupsLoading}
          />
          <Content className="mod-streaming-body">
            <div className="streaming-body-content">
              <VideoGrid
                activeStreams={activeStreams}
                onRemoveStream={handleRemoveStream}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
              />
            </div>
          </Content>
        </Layout>
      </div>
    </>
  );
};

export default ModStreaming;
